// AB#1818 — Super Admin "New category" modal. Implements Dina design-handoff
// §5.4: EN + AR name fields side-by-side, drag-drop image dropzone with
// keyboard-reachable hidden <input type="file">, live preview, isActive
// toggle, submit via POST /api/Category/admin/create (multipart). All copy
// flows through `t()` and dimensions follow the §15 photography brief
// (max 2 MB, ≥200×200 px, JPG/PNG/WebP).
//
// AB#1823 — extended to double as the EDIT modal. When a `category` prop is
// supplied the modal pre-fills name/nameAr/current image, makes the new image
// optional (omit to keep the existing one), submits via PUT /admin/{id}, and
// shows an advisory rename-reach line ("renames for N salons") when the display
// name changes. `salonsSubscribed` (N) is read from the impact endpoint.

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Button from '../../../components/Button/Button';
import Toggle from '../../../components/Toggle/Toggle';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../contexts/ToastContext';
import {
    useCreateAdminCategory,
    useUpdateAdminCategory,
    useCategoryImpact,
} from '../../../hooks/useCategoriesAdmin';
import { type Category, CategoryTargetGender } from '../../../types/api-responses';
import { resolveImageUrl } from '../../../utils/imageUrl';
import GenderTargetControl from './GenderTargetControl';
import styles from './CategoryModals.module.css';

interface CreateCategoryModalProps {
    open: boolean;
    onClose: () => void;
    /**
     * AB#1823 — when set, the modal runs in EDIT mode for this category:
     * pre-fills fields, makes the image optional, submits via PUT /admin/{id}.
     * When undefined the modal runs in CREATE mode (original behaviour).
     */
    category?: Category | null;
}

const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const MIN_DIM = 200;
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp'] as const;

interface CreateFormValues {
    name: string;
    nameAr: string;
}

const schema = z.object({
    name: z.string().trim().min(2, 'name').max(60, 'name'),
    nameAr: z.string().trim().min(2, 'nameAr').max(120, 'nameAr'),
});

function formatSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    const kb = bytes / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(2)} MB`;
}

/**
 * Resolve the natural pixel dimensions of an image file. Returns `null` when
 * the file fails to decode (defensive — the browser will not raise but yields
 * a 0x0 box). Used to enforce the ≥200×200 minimum from Dina §5.4.
 */
function getImageDimensions(file: File): Promise<{ width: number; height: number } | null> {
    return new Promise((resolve) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => {
            URL.revokeObjectURL(url);
            resolve({ width: img.naturalWidth, height: img.naturalHeight });
        };
        img.onerror = () => {
            URL.revokeObjectURL(url);
            resolve(null);
        };
        img.src = url;
    });
}

const CreateCategoryModal: React.FC<CreateCategoryModalProps> = ({ open, onClose, category }) => {
    const { t } = useLanguage();
    const { showToast } = useToast();
    const createMutation = useCreateAdminCategory();
    const updateMutation = useUpdateAdminCategory();

    const isEdit = category != null;
    const mutation = isEdit ? updateMutation : createMutation;

    const imageInputId = useId();
    const imageInputRef = useRef<HTMLInputElement>(null);

    const [file, setFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);
    const [isActive, setIsActive] = useState<boolean>(true);
    const [isDragging, setIsDragging] = useState(false);
    // AB#1870 — gender targeting (default No preference). Pre-fills on edit.
    const [targetGender, setTargetGender] = useState<CategoryTargetGender>(
        CategoryTargetGender.NoPreference,
    );

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<CreateFormValues>({
        resolver: zodResolver(schema),
        defaultValues: { name: '', nameAr: '' },
        mode: 'onSubmit',
    });

    // AB#1823 — pre-fill on edit. Existing image is shown as the current
    // thumbnail (resolved URL); a newly picked file overrides it. Re-run when
    // the edited category changes or the modal (re)opens.
    useEffect(() => {
        if (!open) return;
        if (category) {
            reset({ name: category.name, nameAr: category.nameAr ?? '' });
            setIsActive(category.isActive);
            setTargetGender(category.targetGender ?? CategoryTargetGender.NoPreference);
            setFile(null);
            setImageError(null);
        } else {
            reset({ name: '', nameAr: '' });
            setIsActive(true);
            setTargetGender(CategoryTargetGender.NoPreference);
            setFile(null);
            setImageError(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, category?.id]);

    // AB#1823 — rename-reach advisory (N). Only fetched in edit mode while the
    // dialog is open; reads `salonsSubscribed` from the shared impact endpoint.
    const { data: impact } = useCategoryImpact(isEdit && open ? category!.id : null);

    // The existing image, resolved for display when no new file is picked.
    const existingImageUrl = useMemo(
        () => (isEdit ? resolveImageUrl(category?.imageUrl) : null),
        [isEdit, category?.imageUrl],
    );

    // Advisory reach line shows only when the *display name* actually changes.
    const watchedName = watch('name');
    const nameChanged =
        isEdit && watchedName != null && watchedName.trim() !== (category?.name ?? '').trim();

    const resetEverything = useCallback(() => {
        reset({ name: '', nameAr: '' });
        setFile(null);
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setPreviewUrl(null);
        setImageError(null);
        setIsActive(true);
        setTargetGender(CategoryTargetGender.NoPreference);
        setIsDragging(false);
    }, [reset, previewUrl]);

    const handleClose = useCallback(() => {
        if (mutation.isPending) return;
        resetEverything();
        onClose();
    }, [mutation.isPending, resetEverything, onClose]);

    const acceptFile = useCallback(
        async (incoming: File | null | undefined) => {
            if (!incoming) return;
            setImageError(null);
            if (!ACCEPTED.includes(incoming.type as (typeof ACCEPTED)[number])) {
                setImageError(t('superadmin.categories.error.imageType'));
                return;
            }
            if (incoming.size > MAX_BYTES) {
                setImageError(t('superadmin.categories.error.imageTooLarge'));
                return;
            }
            const dims = await getImageDimensions(incoming);
            if (!dims || dims.width < MIN_DIM || dims.height < MIN_DIM) {
                setImageError(t('superadmin.categories.error.imageDimensions'));
                return;
            }
            if (previewUrl) URL.revokeObjectURL(previewUrl);
            setPreviewUrl(URL.createObjectURL(incoming));
            setFile(incoming);
        },
        [t, previewUrl],
    );

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const next = e.target.files?.[0] ?? null;
        acceptFile(next);
    };

    const onDrop = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        setIsDragging(false);
        const next = e.dataTransfer.files?.[0] ?? null;
        acceptFile(next);
    };

    const onDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
        e.preventDefault();
        if (!isDragging) setIsDragging(true);
    };

    const onDragLeave = () => setIsDragging(false);

    const removeFile = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setFile(null);
        setPreviewUrl(null);
        setImageError(null);
        if (imageInputRef.current) imageInputRef.current.value = '';
    };

    const onValidSubmit = handleSubmit(async (values) => {
        // CREATE requires an image; EDIT may keep the existing one (image omitted).
        if (!isEdit && !file) {
            setImageError(t('superadmin.categories.error.imageRequired'));
            return;
        }
        try {
            if (isEdit) {
                await updateMutation.mutateAsync({
                    categoryId: category!.id,
                    data: {
                        name: values.name.trim(),
                        nameAr: values.nameAr.trim(),
                        image: file ?? undefined,
                        isActive,
                        targetGender,
                    },
                });
                showToast(t('superadmin.categories.toast.updated'), 'success');
            } else {
                await createMutation.mutateAsync({
                    name: values.name.trim(),
                    nameAr: values.nameAr.trim(),
                    image: file as File,
                    isActive,
                    targetGender,
                });
                showToast(t('superadmin.categories.toast.created'), 'success');
            }
            resetEverything();
            onClose();
        } catch (err: unknown) {
            const status = (err as { response?: { status?: number } })?.response?.status;
            const msg =
                status === 409
                    ? t('superadmin.categories.error.duplicate')
                    : t('superadmin.categories.toast.error');
            showToast(msg, 'error');
        }
    });

    const nameError = useMemo(() => {
        if (!errors.name) return null;
        return t('superadmin.categories.error.nameRequired');
    }, [errors.name, t]);

    const nameArError = useMemo(() => {
        if (!errors.nameAr) return null;
        return t('superadmin.categories.error.nameArRequired');
    }, [errors.nameAr, t]);

    if (!open) return null;

    return (
        <div
            className={styles.modalOverlay}
            role="presentation"
            onClick={handleClose}
        >
            <div
                className={`${styles.modal} ${styles.modalWide}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-category-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className={styles.modalHeader}>
                    <h2 id="create-category-title" className={styles.modalTitle}>
                        {isEdit
                            ? t('superadmin.categories.edit.title')
                            : t('superadmin.categories.create.title')}
                    </h2>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={handleClose}
                        aria-label={t('superadmin.categories.action.cancel')}
                    >
                        &times;
                    </button>
                </div>

                <form onSubmit={onValidSubmit} noValidate>
                    <div className={styles.modalContent}>
                        <div className={styles.nameRow}>
                            <div>
                                <label htmlFor="create-category-name" className={styles.label}>
                                    {t('superadmin.categories.create.name')} *
                                </label>
                                <input
                                    id="create-category-name"
                                    type="text"
                                    className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
                                    autoComplete="off"
                                    {...register('name')}
                                />
                                {nameError && (
                                    <p className={styles.fieldError} role="alert">
                                        {nameError}
                                    </p>
                                )}
                            </div>
                            <div>
                                <label htmlFor="create-category-name-ar" className={styles.label}>
                                    {t('superadmin.categories.create.nameAr')} *
                                </label>
                                <input
                                    id="create-category-name-ar"
                                    type="text"
                                    dir="rtl"
                                    className={`${styles.input} ${errors.nameAr ? styles.inputError : ''}`}
                                    autoComplete="off"
                                    {...register('nameAr')}
                                />
                                {nameArError && (
                                    <p className={styles.fieldError} role="alert">
                                        {nameArError}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* AB#1823 — advisory rename-reach line. Shown only when the
                            display name changes and at least one salon is subscribed
                            (N from the impact endpoint). Advisory, non-blocking. */}
                        {nameChanged && impact && impact.salonsSubscribed > 0 && (
                            <p className={styles.renameReach} role="status">
                                {t('superadmin.categories.edit.renameReach').replace(
                                    '{count}',
                                    String(impact.salonsSubscribed),
                                )}
                            </p>
                        )}

                        {/* AB#1870 — gender targeting (identity metadata, same
                            tier as the name). Default = No preference; never
                            blocks submit. Identical in create + edit. */}
                        <div className={styles.field}>
                            <span id="create-category-gender-label" className={styles.label}>
                                {t('superadmin.categories.gender.label')}
                            </span>
                            <GenderTargetControl
                                value={targetGender}
                                onChange={setTargetGender}
                                labelId="create-category-gender-label"
                            />
                            <p className={styles.hint}>
                                {t('superadmin.categories.gender.hint')}
                            </p>
                        </div>

                        <div className={styles.field}>
                            <label htmlFor={imageInputId} className={styles.label}>
                                {t('superadmin.categories.create.image')}
                                {isEdit ? '' : ' *'}
                            </label>
                            {/* Hidden tab-reachable input — see Mona §5.4 a11y notes. */}
                            <input
                                id={imageInputId}
                                ref={imageInputRef}
                                type="file"
                                accept={ACCEPTED.join(',')}
                                className={styles.visuallyHidden}
                                aria-describedby="create-category-image-hint"
                                onChange={onFileInputChange}
                            />
                            <label
                                htmlFor={imageInputId}
                                className={styles.dropzoneLabel}
                                onDrop={onDrop}
                                onDragOver={onDragOver}
                                onDragLeave={onDragLeave}
                            >
                                <div
                                    className={`${styles.dropzone} ${isDragging ? styles.dropzoneActive : ''}`}
                                >
                                    <p className={styles.dropzoneTitle}>
                                        {isEdit
                                            ? t('superadmin.categories.edit.image.replace')
                                            : t('superadmin.categories.create.image.drop')}
                                    </p>
                                    <p
                                        id="create-category-image-hint"
                                        className={styles.dropzoneHint}
                                    >
                                        {t('superadmin.categories.create.image.hint')}
                                    </p>
                                </div>
                            </label>
                            {imageError && (
                                <p className={styles.fieldError} role="alert">
                                    {imageError}
                                </p>
                            )}
                            {file && previewUrl && (
                                <div className={styles.preview}>
                                    <img
                                        src={previewUrl}
                                        alt=""
                                        className={styles.previewImage}
                                    />
                                    <div className={styles.previewMeta}>
                                        <p className={styles.previewName}>{file.name}</p>
                                        <p className={styles.previewSize}>{formatSize(file.size)}</p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.removeButton}
                                        onClick={removeFile}
                                    >
                                        {t('superadmin.categories.create.image.remove')}
                                    </button>
                                </div>
                            )}
                            {/* AB#1823 — current image when editing and no new file
                                has been picked. Picking a file replaces it; keeping
                                it omits `image` from the PUT so the BE preserves it. */}
                            {isEdit && !file && existingImageUrl && (
                                <div className={styles.preview}>
                                    <img
                                        src={existingImageUrl}
                                        alt=""
                                        className={styles.previewImage}
                                    />
                                    <div className={styles.previewMeta}>
                                        <p className={styles.previewName}>
                                            {t('superadmin.categories.edit.image.current')}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className={styles.toggleRow}>
                            <Toggle
                                label={t('superadmin.categories.create.activeNow')}
                                checked={isActive}
                                onChange={setIsActive}
                            />
                        </div>

                        {!isEdit && (
                            <p className={styles.notice}>
                                {t('superadmin.categories.create.autoApprovedNotice')}
                            </p>
                        )}
                    </div>

                    <div className={styles.modalActions}>
                        <Button
                            label={t('superadmin.categories.action.cancel')}
                            variant="secondary"
                            onClick={handleClose}
                            disabled={mutation.isPending}
                            type="button"
                        />
                        <Button
                            label={
                                isEdit
                                    ? t('superadmin.categories.edit.submit')
                                    : t('superadmin.categories.create.submit')
                            }
                            // CREATE needs a file; EDIT can keep the existing image.
                            disabled={mutation.isPending || (!isEdit && !file)}
                            type="submit"
                        />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateCategoryModal;
